import type { TechnicianAssignmentEntity } from '../entities';
import type { TechnicianNameFields } from './types';

import { StsBackendOperation } from '../../../sts-backend-operation';
import { formatFullName } from '../../cases/helpers';

/**
 * Parameters for toggling a technician assignment's completion status. Requires the full
 * assignments list so the target assignment can be looked up by ID to determine its current
 * completion state.
 */
export type ToggleCompletionParams = {
    /** The current user's account, used to build the technician name. */
    account: TechnicianNameFields | undefined;

    /** The ID of the assignment to toggle. */
    assignmentId: string;

    /** The current list of assignments (used to look up the target assignment). */
    assignments: TechnicianAssignmentEntity[];
};

/**
 * Toggle a technician assignment between completed and not-completed. Looks up the assignment
 * by ID, builds the technician display name from the current user's account, and calls the API
 * to persist the new completion status.
 */
export class ToggleCompletion extends StsBackendOperation {
    /**
     * Toggle an assignment's completion flag and persist the change.
     *
     * @param params - the assignment ID, current assignments list, and user account
     * @throws {Error} when no assignment matches the given ID
     */
    async exec(params: ToggleCompletionParams): Promise<void> {
        const { account, assignmentId, assignments } = params;

        // Find the target assignment in the current list
        const assignment = assignments.find((a) => a.caseTechnicianAssignmentId === assignmentId);

        if (!assignment) {
            throw new Error(`No assignment found for id: ${assignmentId}`);
        }

        // Build the technician display name from account fields
        const technicianName = formatFullName(account?.firstName || '', account?.lastName || '');

        // Persist the toggled completion status
        await this.client.updateAssignmentCompletionStatus({
            casetechnicianassignmentid: assignmentId,
            completed: !assignment.isCompleted,
            technicianname: technicianName,
        });
    }
}
